import { z } from "zod";
import type { ImageUpscaleProvider, PipelineNode, PipelineSpec, ToolCore } from "@loumy/contracts";
import { CAP } from "@loumy/capabilities";

// §10/D163 Image-Upscale — eigenständiges Einzelbild-Tool: Super-Resolution via **Real-ESRGAN**
// (fal). Bild rein → hochskaliertes Bild raus (treu, kein Prompt, keine Halluzination). KEINE
// Generate-Phase → startet direkt in der Server-Post-Process-Phase (wie das bg-remove-Tool, nur
// Super-Resolution statt AI-Matting). Der Provider wird im Build gepinnt (Build==Laufzeit, D34).
//
// Settings: `scale` (2× oder 4×) + `variant` (allgemein = Foto/3D; anime = saubere Kanten für
// Sprites/Pixel-Art). Beide werden als Literale in den Knoten-`params` eingefroren (D34) und über
// den Provider (fal-esrgan) auf das konkrete ESRGAN-Netz + den `scale`-Param abgebildet. Preis-
// neutral gegenüber den Settings (generischer flat-Key `image-upscale`, D101/D150).

export const UPSCALE_SCALES = ["2", "4"] as const;
export const UPSCALE_VARIANTS = ["general", "anime"] as const;

export const ImageUpscaleSettings = z.object({
  // String-Enum (nicht numerisch): die Settings-Introspektion (§3.4) klassifiziert nur ZodEnum als
  // Auswahlfeld — buildPipeline parst es zum Zahl-Faktor. Default 4× (der „mach es größer"-Default).
  scale: z.enum(UPSCALE_SCALES).default("4"),
  variant: z.enum(UPSCALE_VARIANTS).default("general"),
});
export type ImageUpscaleSettings = z.infer<typeof ImageUpscaleSettings>;

export const imageUpscaleCore: ToolCore<ImageUpscaleSettings> = {
  id: "image-upscale",
  category: "image",
  inputs: [{ kind: "image", min: 1, max: 1, role: "source" }],
  settingsSchema: ImageUpscaleSettings,
  outputSlots: [{ name: "image", kind: "image", optional: false }],
  buildPipeline(settings, _inputs, ctx): PipelineSpec {
    // D34: Provider gepinnt — der Default-Adapter des image-upscale-Slots (Real-ESRGAN).
    const esrgan = ctx.providers.resolve<ImageUpscaleProvider>("image-upscale");
    const scale = settings.scale === "2" ? 2 : 4; // String-Enum → Zahl-Faktor (eingefroren, D34)
    const nodes: PipelineNode[] = [
      {
        id: "upscale",
        capabilityId: CAP.imageUpscale.id,
        capabilityVersion: CAP.imageUpscale.version,
        surface: "server",
        inputs: { image: { node: "$input", port: "source" } },
        params: { scale, variant: settings.variant, providerId: esrgan.id, providerVersion: esrgan.version },
      },
    ];
    return { nodes, outputs: { image: { ref: { node: "upscale", port: "image" } } } };
  },
};
